import { css } from "@emotion/react";
import { useLocaleState } from "@/hooks/useLocaleState";
import { useStorefrontSettingsState } from "@/hooks/useStorefrontSettingsState";
import { CustomScrollbars } from "../components/CustomScrollbars/CustomScrollbars";
import { LandingPage } from "../components/ProductBrowser/LandingPage";
import { SiteLayout } from "../components/SiteLayout";
import { useCatalogState } from "../hooks/useCatalogState";
import { useTailwindMediaQueries } from "../hooks/useTailwindMediaQueries";
import { useThemeState } from "../hooks/useThemeState";

// Home Page
export const HomePage = () => {
	const catalogState = useCatalogState();
	const themeState = useThemeState();
	const localeState = useLocaleState();
	const isDarkTheme = themeState.isDark();
	const catalogs =
		(catalogState.get().catalogs && catalogState.get().catalogs) ?? [];
	const storefrontSettingsState = useStorefrontSettingsState();
	const homepageHeroSectionHeading1 = localeState.translate(
		storefrontSettingsState.get().settings?.HomepageHeroSectionHeading1 || {
			en: "",
			es: "",
			fr: "",
		},
	);
	const homepageHeroSectionHeading2 = localeState.translate(
		storefrontSettingsState.get().settings?.HomepageHeroSectionHeading2 || {
			en: "",
			es: "",
			fr: "",
		},
	);
	const homepageHeroSectionHeading3 = localeState.translate(
		storefrontSettingsState.get().settings?.HomepageHeroSectionHeading3 || {
			en: "",
			es: "",
			fr: "",
		},
	);
	const homepageShowHeroSection =
		storefrontSettingsState.get().settings?.HomepageShowHeroSection || true;
	const homepageShowFooter =
		storefrontSettingsState.get().settings?.HomepageShowFooter || true;
	const homepageFooterText = localeState.translate(
		storefrontSettingsState.get().settings?.HomepageFooterText || {
			en: "",
			es: "",
			fr: "",
		},
	);

	const mediaQueries = useTailwindMediaQueries();
	const homepageCatalogItemWidth =
		storefrontSettingsState.get().settings?.HomepageCatalogItemWidth || "lg";
	let homepageCatalogWidthPercent = 45;
	if (homepageCatalogItemWidth === "sm") {
		homepageCatalogWidthPercent = 20;
	} else if (homepageCatalogItemWidth === "md") {
		homepageCatalogWidthPercent = 25;
	} else if (homepageCatalogItemWidth === "lg") {
		homepageCatalogWidthPercent = 45;
	} else if (homepageCatalogItemWidth === "xl") {
		homepageCatalogWidthPercent = 100;
	}
	if (mediaQueries.phone) {
		homepageCatalogWidthPercent = 100;
	}

	let boxShadow = "unset";
	let hoverBoxShadow = "unset";
	let border = "unset";
	if (homepageShowHeroSection) {
		boxShadow = isDarkTheme
			? "0 8px 32px rgba(0,0,0,0.3)"
			: "0 8px 32px rgba(0,0,0,0.1)";
		hoverBoxShadow = isDarkTheme
			? "0 12px 40px rgba(0,0,0,0.4)"
			: "0 12px 40px rgba(0,0,0,0.15)";
		border = isDarkTheme ? "rgba(255,255,255,0.1)" : "rgba(255,255,255,0.2)";
	}

	return (
		<SiteLayout
			title="Home"
			mainChildren={
				<CustomScrollbars>
					<div
						css={css`
                        min-height: 100vh;
                        padding: 0 0 2rem 0;
                    `}
					>
						{/* Hero Section */}
						{homepageShowHeroSection && (
							<div
								css={css`
                                text-align: center;
                                padding: 3rem 1rem;
                                margin-bottom: 2rem;
                            `}
							>
								<div
									css={css`
                                    font-size: 2.7rem;
                                    font-weight: 700;
                                    margin-bottom: 1rem;
                                    color: ${isDarkTheme ? "#fff" : "#2c3e50"};
                                    text-shadow: ${isDarkTheme ? "0 2px 4px rgba(0,0,0,0.3)" : "0 2px 4px rgba(0,0,0,0.1)"};
                                
                                    @media (max-width: 768px) {
                                        font-size: 2rem;
                                    }
                                
                                    @media (max-width: 480px) {
                                        font-size: 1.5rem;
                                    }
                                `}
								>
									{homepageHeroSectionHeading1 && (
										<div css={css`font-size:.5em;line-height:1.5em;`}>
											{homepageHeroSectionHeading1}
										</div>
									)}
									{homepageHeroSectionHeading2 && (
										<h1 css={css`all: unset;display:block;`}>
											{homepageHeroSectionHeading2}
										</h1>
									)}
									{homepageHeroSectionHeading3 && (
										<h2
											css={css`
                                            all: unset;
                                            display:block;
                                            padding: .3em 0 0 0;
                                            font-size: .5em;
                                            font-weight: 500;
                                            color: ${isDarkTheme ? "#bdc3c7" : "#6c757d"};
                                            max-width: 600px;
                                            margin: 0 auto;
                                            line-height: 1.5em;
                                        `}
										>
											{homepageHeroSectionHeading3}
										</h2>
									)}
								</div>
							</div>
						)}

						{/* Catalogs Grid */}
						<div
							css={css`
                             max-width: 1400px;
                             margin: 0 auto;
                             padding: 0 1rem;
                             
                             @media (max-width: 768px) {
                                 padding: 0 0.5rem;
                             }
                         `}
						>
							<div
								css={css`
                                 display: flex;
                                 flex-wrap: wrap;
                                 gap: 2rem;
                                 justify-content: center;
                                 
                                 @media (max-width: 1024px) {
                                     grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
                                     gap: 1.5rem;
                                 }
                                 
                                 @media (max-width: 768px) {
                                     grid-template-columns: 1fr;
                                     gap: 1rem;
                                     width: 100%;
                                     max-width: 100%;
                                 }
                             `}
							>
								{catalogs.map((catalog) => (
									<div
										key={catalog.Id}
										css={css`
                                         width: 100%;
                                         min-width: 320px;
                                         flex: 0 0 ${homepageCatalogWidthPercent}%;
                                         box-sizing: border-box;
                                         border-radius: 16px;
                                         padding: 2rem;

                                         /*box-shadow: ${boxShadow};
                                         backdrop-filter: blur(10px);*/
                                         border: 1px solid ${border};
                                         transition: all 0.3s ease;
                                         
                                         &:hover {
                                             box-shadow: ${hoverBoxShadow};
                                         }
                                         
                                         @media (max-width: 768px) {
                                             max-width: 100%;
                                             padding: 1rem;
                                             margin: 0;
                                             box-sizing: border-box;
                                         }
                                     `}
									>
										<LandingPage
											catalog={catalog}
											showCatalogName={true}
											showCategoryTreeGrid={false}
										/>
									</div>
								))}
							</div>
						</div>

						{/* Footer Section */}
						{homepageShowFooter && (
							<div
								css={css`
                                text-align: center;
                                padding: 3rem 1rem 1rem;
                                margin-top: 3rem;
                                border-top: 1px solid ${
																	isDarkTheme
																		? "rgba(255,255,255,0.1)"
																		: "rgba(0,0,0,0.1)"
																};
                            `}
							>
								<p
									css={css`
                                    color: ${isDarkTheme ? "#95a5a6" : "#6c757d"};
                                    font-size: 0.9rem;
                                    margin-bottom: 0.5rem;
                                `}
								>
									{homepageFooterText}
								</p>
								<p
									css={css`
                                    display:none;
                                    color: ${isDarkTheme ? "#7f8c8d" : "#dddddd"};
                                    font-size: 0.6rem;
                                `}
								>
									<a
										href="https://dbworks.com/products/rentalworks/"
										css={css`color:currentColor;text-decoration:none;&:hover{text-decoration:underline;}`}
									>
										RentalWorks Storefront
									</a>
									<br />© {new Date().getFullYear().toString()}{" "}
									<a
										href="https://dbworks.com"
										css={css`color:currentColor;text-decoration:none;&:hover{text-decoration:underline;}`}
									>
										Database Works Inc.
									</a>{" "}
									All rights reserved.
								</p>
							</div>
						)}
					</div>
				</CustomScrollbars>
			}
		/>
	);
};
//-------------------------------------------------------------------------------------------------------
